import { useEffect, useMemo } from 'react';
import { useContent } from './content.jsx';
import { useI18n } from './i18n/index.jsx';
import { sectorDefaults } from './sectorsData.js';

// The catalogue (built from the category tree), 4 levels: sector ›
// department › sub-category › product type, with page addresses in French
// (/secteurs/…) and English (/sectors/…). Falls back to the built-in
// sectors when nothing is published.

export const slugify = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
// Same rule as the server (content/catalogue.js) — keep in step.
export const pageSlug = (x, lang) => (lang === 'en' ? x.slugEn || slugify(x.name?.en || x.name?.fr) : x.slugFr || slugify(x.name?.fr || x.name?.en)) || x.id;
const prefix = (lang) => (lang === 'en' ? 'sectors' : 'secteurs');
export const deptPath = (s, d, lang) => `/${prefix(lang)}/${s.key}/${pageSlug(d, lang)}`;
export const subPath = (s, d, sub, lang) => `${deptPath(s, d, lang)}/${pageSlug(sub, lang)}`;
export const typePath = (s, d, sub, ty, lang) => `${subPath(s, d, sub, lang)}/${pageSlug(ty, lang)}`;
const matches = (x, slug) => slug && [pageSlug(x, 'fr'), pageSlug(x, 'en'), x.id].includes(slug);

// Options of a spec group: [{ value (French, stored), label (visitor's language) }].
export function specOptions(group, lang) {
  const fr = String(group?.options?.fr || '').split(',').map((x) => x.trim()).filter(Boolean);
  const en = String(group?.options?.en || '').split(',').map((x) => x.trim());
  return fr.map((v, i) => ({ value: v, label: lang === 'en' ? en[i] || v : v }));
}

export function useCatalogue() {
  const { page } = useContent('sectors');
  return useMemo(() => {
    const items = (page?.items?.length ? page.items : sectorDefaults().items).filter((s) => !s.hidden && !(s.hideIfEmpty && !(s.departments || []).length))
      .map((s) => ({ ...s, departments: (s.departments || []).map((d) => ({ ...d, subs: d.subs || [] })) }));
    const types = new Map(); // product type id → { s, d, sub, ty }
    for (const s of items) for (const d of s.departments) for (const sub of d.subs) for (const ty of sub.types || []) if (!ty.hidden) types.set(ty.id, { s, d, sub, ty });
    return {
      sectors: items,
      types,
      find(sectorKey, deptSlug, subSlug, typeSlug) {
        const s = items.find((x) => x.key === sectorKey);
        const d = s && s.departments.find((x) => matches(x, deptSlug));
        const sub = d && subSlug ? d.subs.find((x) => matches(x, subSlug)) : null;
        const ty = sub && typeSlug ? (sub.types || []).find((x) => !x.hidden && matches(x, typeSlug)) : null;
        return { s, d, sub, ty };
      },
      // Where a page is in a sector from the last part of an address (older
      // addresses, before the 4 levels): its current path, or null.
      locate(sectorKey, slug, lang) {
        const s = items.find((x) => x.key === sectorKey);
        if (!s || !slug) return null;
        for (const d of s.departments) {
          if (matches(d, slug)) return deptPath(s, d, lang);
          for (const sub of d.subs) {
            if (matches(sub, slug)) return subPath(s, d, sub, lang);
            for (const ty of sub.types || []) if (!ty.hidden && matches(ty, slug)) return typePath(s, d, sub, ty, lang);
          }
        }
        return null;
      },
    };
  }, [page]);
}

// Page title + meta description (FR/EN) for single-page navigation.
export function usePageMeta(title, description) {
  const { t } = useI18n();
  useEffect(() => {
    if (!title) return undefined;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    document.title = `${title} | ${t('brand.name')}`;
    if (meta && description) meta.setAttribute('content', description.slice(0, 300));
    return () => { document.title = prevTitle; if (meta && prevDesc) meta.setAttribute('content', prevDesc); };
  }, [title, description, t]);
}

// Chip value made from a data-table cell (same rule as the server): commas
// would split the option list, so "2,5" → "2.5" and other commas → spaces.
export const chipValue = (v) => String(v || '').replace(/(\d),(\d)/g, '$1.$2').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

// Technical content of a product type (tables + picture sections, in
// order). Types saved before picture sections only had `tables`.
export const techOf = (ty) => (Array.isArray(ty?.tech) ? ty.tech : (Array.isArray(ty?.tables) ? ty.tables.map((x) => ({ ...x, kind: 'table' })) : []));
